import React, { useCallback, useMemo, useRef } from 'react';
import { View, Text, FlatList, Animated, Pressable, Platform, Image, RefreshControl, TouchableOpacity, StyleSheet } from 'react-native';
import LinkGenModal from '../CaptureScreen/Components/LinkGenModal';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { AntDesign, Entypo, FontAwesome5, FontAwesome6, Ionicons, MaterialIcons } from '@expo/vector-icons';
import { useQuery } from '@tanstack/react-query';
import apis, { duplicateEmailTemplate, duplicateChildTemplate, generateDocumentTemplateWithAI, postDocumentTemplate } from '@api/api';
import globalStyles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import ErrorBoundary from '@utils/ErrorBoundary';
import { AlertDialog, Input, Button, Popover, Separator, XStack, YStack } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import parseByDelimiter from '@utils/parseByDelimiter';
import moment from 'moment';
import { useDeviceType } from '@hooks/useDeviceType';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useSelector } from 'react-redux';
import DocumentTemplateHeader from './components/DocumentTemplateHeader';
import { HOST } from '@api/constant';

const DocumentTemplate = ({ navigation }) => {
  const emailTemplateRef = React.useRef(null);
  const childTitleRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const theme = themeSelector();
  const [emailTemplates, setEmailTemplates] = React.useState([]);
  const { isTablet } = useDeviceType();
  const tablet = isTablet;
  const [refreshing, setRefreshing] = React.useState(false);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const currentUser = useSelector((state: DefaultRootState) => state.promiseData.data);
  const linkGenModalRef = useRef<any>(null);

  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['documentTemplate'],
    queryFn: async () => {
      const data = await apis.getDocumentTemplates()
        .then(res => {
          return res
        });
      return data
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    refetch().then(() => setRefreshing(false));
  }, [refreshing, refetch]);

  const duplicateTemplate = useCallback(async (item: { _id: any; }, asChild: boolean, title?: string,) => {
    if (asChild) {
      await duplicateChildTemplate(item._id, title).then(() => { refetch() })
    } else {
      await duplicateEmailTemplate(item._id).then(() => { refetch() })
    }
  }, [refetch]);

  const toCapitalCase = useCallback((name: string) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }, []);

  const getStatusColor = useCallback((status: string, theme: boolean) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return theme ? '#10b981' : '#34d399';
      case 'completed':
        return theme ? '#3b82f6' : '#60a5fa';
      default:
        return theme ? '#6b7280' : '#9ca3af';
    }
  }, []);

  const getStatusBg = useCallback((status: string, theme: boolean) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return theme ? 'rgba(16, 185, 129, 0.1)' : 'rgba(52, 211, 153, 0.2)';
      case 'completed':
        return theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(96, 165, 250, 0.2)';
      default:
        return theme ? 'rgba(107, 114, 128, 0.1)' : 'rgba(156, 163, 175, 0.2)';
    }
  }, []);

  const RenderItem = useCallback(({ onShare, item, hideDuplicate, index, linkedChildren, isChild = false }) => {

    const handleShare = useCallback(() => {
      onShare?.(item);
    }, [onShare, item]);

    return item && (
      <ErrorBoundary>
        <View style={[
          localStyles.documentContainer,
          isChild && localStyles.childDocumentContainer, { overflow: "visible", flex: 1, alignSelf: "stretch" }
        ]}>
          <View style={[
            globalStyles.modernCard,
            { overflow: "visible" },
            theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
            localStyles.documentCard,
            { flexDirection: 'row', gap: 20, alignItems: 'stretch' }
          ]}>
            {/* Preview Image on the Left (Web only) */}
            {Platform.OS === 'web' && item.snapShot && (
              <TouchableOpacity
                onPress={() => { previewRef.current?.openModal?.(item) }}
                style={[
                  localStyles.previewContainer,
                  theme ? localStyles.previewLight : localStyles.previewDark,
                  {
                    minWidth: 200,
                    width: 200,
                    minHeight: 300,
                    marginTop: 0,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 5
                  }
                ]}
              >
                <Image
                  source={{ uri: item.snapShot }}
                  style={localStyles.previewImage}
                />
              </TouchableOpacity>
            )}

            {/* Content on the Right */}
            <View style={{ flex: 1, gap: 16 }}>
              {/* Header Section with Actions */}
              <View style={[localStyles.headerSection, { overflow: "visible" }]}>
                {/* Status Badge */}
                <View style={[
                  localStyles.statusBadge,
                  {
                    position: 'absolute',
                    top: -10,
                    right: -20,
                    backgroundColor: getStatusBg(item.status, theme),
                    borderColor: getStatusColor(item.status, theme),
                  }
                ]}>
                  <Text style={[
                    localStyles.statusText,
                    { color: getStatusColor(item.status, theme) }
                  ]}>
                    {toCapitalCase(item.status) || "DRAFT"}
                  </Text>
                </View>

                {/* Actions Toolbar */}
                <XStack gap={10} alignItems="center" backgroundColor="rgba(0,0,0,0.2)" padding={8} borderRadius={12}>
                  <Pressable onPress={() => navigation.navigate('DocumentTemplateEditor', { templateId: item._id })}>
                    <MaterialIcons name="edit" size={18} color="white" />
                  </Pressable>

                  <Pressable onPress={handleShare}>
                    <MaterialIcons name="share" size={18} color="white" />
                  </Pressable>

                  {!hideDuplicate && (
                    <Popover allowFlip stayInFrame>
                      <Popover.Trigger>
                        <MaterialIcons name="content-copy" size={18} color="white" />
                      </Popover.Trigger>
                      <Popover.Content borderWidth={0} unstyled elevate zIndex={100}
                        style={{ borderRadius: 20, backgroundColor: "#ececec", padding: 10, minWidth: 200 }}
                      >
                        <YStack gap={10}>
                          {/* Duplicate as Child Dialog */}
                          <AlertDialog native>
                            <AlertDialog.Trigger asChild>
                              <Pressable style={{ padding: 10 }}>
                                <Text style={{ color: 'black', fontWeight: 'bold' }}>Duplicate as child</Text>
                                <Text style={{ color: '#666', fontSize: 12 }}>Create variant</Text>
                              </Pressable>
                            </AlertDialog.Trigger>
                            <AlertDialog.Portal>
                              <AlertDialog.Overlay opacity={0.5} style={{ zIndex: 1000 }} />
                              <AlertDialog.Content bordered elevate style={{ padding: 20, borderRadius: 16, minWidth: 300, gap: 16 }}>
                                <AlertDialog.Title>Duplicate Template</AlertDialog.Title>
                                <Input ref={childTitleRef} placeholder="Child template name" />
                                <XStack gap={10} justifyContent="flex-end">
                                  <AlertDialog.Cancel asChild><Button>Cancel</Button></AlertDialog.Cancel>
                                  <AlertDialog.Action asChild>
                                    <Button onPress={async () => {
                                      const input = childTitleRef.current;
                                      const val = input && 'value' in input ? (input as any).value : '';
                                      await duplicateTemplate(item, true, val);
                                    }} theme="active">Duplicate</Button>
                                  </AlertDialog.Action>
                                </XStack>
                              </AlertDialog.Content>
                            </AlertDialog.Portal>
                          </AlertDialog>
                          <Separator />
                          <Pressable onPress={() => duplicateTemplate(item, false)} style={{ padding: 10 }}>
                            <Text style={{ color: 'black', fontWeight: 'bold' }}>Duplicate new</Text>
                            <Text style={{ color: '#666', fontSize: 12 }}>Create independent copy</Text>
                          </Pressable>
                        </YStack>
                      </Popover.Content>
                    </Popover>
                  )}

                  <Pressable>
                    <MaterialIcons name="delete" size={18} color="#ff6b6b" />
                  </Pressable>
                </XStack>
              </View>

              {/* Content Section */}
              <View style={localStyles.contentSection}>
                {/* Title */}
                <Text style={[
                  localStyles.titleText,
                  { color: theme ? '#1e293b' : '#f1f5f9' }
                ]} numberOfLines={2}>
                  {parseByDelimiter(item?.title)}
                </Text>

                {/* Date Section */}
                <View style={localStyles.metaSection}>
                  <View style={localStyles.metaItem}>
                    <MaterialIcons
                      name="event"
                      size={16}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                    <Text style={[
                      localStyles.metaLabel,
                      { color: theme ? '#64748b' : '#94a3b8' }
                    ]}>
                      Created
                    </Text>
                    <Text style={[
                      localStyles.metaValue,
                      { color: theme ? '#1e293b' : '#f1f5f9' }
                    ]}>
                      {moment(item.createdAt).format(globalDate)}
                    </Text>
                  </View>

                  {item?.type && (
                    <View style={localStyles.metaItem}>
                      <MaterialIcons
                        name={item.type === 'project' ? 'folder' : 'description'}
                        size={16}
                        color={theme ? '#64748b' : '#94a3b8'}
                      />
                      <Text style={[
                        localStyles.metaValue,
                        { color: theme ? '#1e293b' : '#f1f5f9' }
                      ]}>
                        {toCapitalCase(item.type)}
                      </Text>
                    </View>
                  )}
                </View>

                {/* Description */}
                {item.description && (
                  <View style={[
                    localStyles.descriptionContainer,
                    theme ? localStyles.descriptionLight : localStyles.descriptionDark
                  ]}>
                    <Text style={[
                      localStyles.descriptionText,
                      { color: theme ? '#64748b' : '#94a3b8' }
                    ]} numberOfLines={3}>
                      {item.description}
                    </Text>
                  </View>
                )}
                {/* Created By */}
                {item.createdBy && (
                  <View style={[
                    localStyles.creatorContainer,
                    theme ? localStyles.creatorLight : localStyles.creatorDark
                  ]}>
                    <View style={[
                      localStyles.avatarCircle,
                      theme ? localStyles.avatarLight : localStyles.avatarDark
                    ]}>
                      <Text style={localStyles.avatarText}>
                        {item.createdBy?.profile_information?.name?.charAt(0)?.toUpperCase() ||
                          item.createdBy?.profile_information?.surname?.charAt(0)?.toUpperCase() ||
                          '?'}
                      </Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[
                        localStyles.creatorLabel,
                        { color: theme ? '#64748b' : '#94a3b8' }
                      ]}>
                        Created by
                      </Text>
                      <Text style={[
                        localStyles.creatorName,
                        { color: theme ? '#1e293b' : '#f1f5f9' }
                      ]}>
                        {[
                          toCapitalCase(item.createdBy?.profile_information?.name),
                          toCapitalCase(item.createdBy?.profile_information?.surname)
                        ].filter(Boolean).join(' ') || 'Unknown'}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Nested Children with Modern Connector */}
        {linkedChildren && linkedChildren.length > 0 && (
          <View style={localStyles.childrenContainer}>
            {linkedChildren.map((child, childIndex) => (
              <View key={`child-${child._id}-${childIndex}`} style={localStyles.childItemWrapper}>
                {/* Modern Tree Connector */}
                <View style={localStyles.connectorContainer}>
                  <View style={[
                    localStyles.connectorLine,
                    { backgroundColor: theme ? 'rgba(59, 130, 246, 0.3)' : 'rgba(96, 165, 250, 0.3)' }
                  ]} />
                  <View style={[
                    localStyles.connectorDot,
                    { backgroundColor: theme ? '#3b82f6' : '#60a5fa' }
                  ]} />
                </View>

                <View style={localStyles.childItemContent}>
                  <RenderItem
                    hideDuplicate
                    item={child}
                    index={childIndex}
                    linkedChildren={data?.filter(item => item.parent === child._id)}
                    isChild={true}
                    onShare={onShare}
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </ErrorBoundary>
    )
  }, [theme, duplicateTemplate, toCapitalCase, getStatusColor, getStatusBg, data]);

  // Calculate document counts
  const totalDocuments = data?.length || 0;
  const activeDocuments = data?.filter(d => d.status === 'active').length || 0;
  const completedDocuments = data?.filter(d => d.status === 'completed').length || 0;
  const inProgressDocuments = data?.filter(d => d.status === 'in-progress' || !d.status).length || 0;

  // Get root level documents: those without a parent OR whose parent is not in the dataset
  const rootDocuments = useMemo(() => {
    if (!data) return [];
    const documentIds = new Set(data.map(doc => doc._id));
    return data.filter(item => !item.parent || !documentIds.has(item.parent));
  }, [data]);

  return (
    <DefaultScreen
      containerStyle={{ zIndex: -1 }}
      // scrollDisabled
      aiSearch
      aiCallback={async (text: string) => {
        try {
          // Generate template using AI
          const response = await generateDocumentTemplateWithAI(text);

          if (response?.success && response?.data) {
            const templateData = response.data;

            // Save the generated template
            const saveResponse = await postDocumentTemplate({
              title: templateData.title,
              description: templateData.description,
              content: templateData.content,
              tags: templateData.tags || [],
              isTemplate: true,
              createdBy: currentUser?._id,
              data: {
                placeholders: templateData.placeholders || [],
                generatedBy: 'ai',
                metadata: templateData.metadata
              }
            });

            // Refresh the list
            await refetch();

            // Navigate to editor with the new template
            if (saveResponse?.data?.document?._id) {
              navigation.navigate('DocumentTemplateEditor', {
                templateId: saveResponse.data.document._id
              });
            } else {
              console.warn('Template saved but no ID returned');
            }
          } else {
            console.error('Failed to generate template:', response);
          }
        } catch (error) {
          console.error('❌ Error generating document template:', error);
        }
      }}
      backButton
      style={{ ...globalStyles.modernMainContent }}
      searchBar={{
        aiSearch: true,
        aiCallback: async (text: string) => {
          try {
            console.log('📄 Generating document template with AI:', text);

            // Generate template using AI
            const response = await generateDocumentTemplateWithAI(text);

            if (response?.success && response?.data) {
              const templateData = response.data;

              // Save the generated template
              const saveResponse = await postDocumentTemplate({
                title: templateData.title,
                description: templateData.description,
                content: templateData.content,
                tags: templateData.tags || [],
                isTemplate: true,
                data: {
                  placeholders: templateData.placeholders || [],
                  generatedBy: 'ai',
                  metadata: templateData.metadata
                }
              });

              // Refresh the list
              await refetch();

              // Navigate to editor with the new template
              if (saveResponse?.data?.document?._id) {
                navigation.navigate('DocumentTemplateEditor', {
                  templateId: saveResponse.data.document._id
                });
              } else {
                console.warn('Template saved but no ID returned');
              }
            } else {
              console.error('Failed to generate template:', response);
            }
          } catch (error) {
            console.error('❌ Error generating document template:', error);
          }
        },
        customIcons: [{
          function: () => navigation.navigate('DocumentTemplateEditor', { templateId: "" }),
          functionName: 'createEmailTemplate',
          icon: <MaterialIcons name="add" size={16} color="white" />
        }, {
            //Refresh button
            function: onRefresh,
            functionName: 'refresh',
            icon: <MaterialIcons name="refresh" size={16} color="white" />
        }]
      }}
    >
      <LinkGenModal title='Share Document' ref={linkGenModalRef} url={__DEV__ ? `${HOST}/documents/sign` : "https://api.smartsaas.pro/documents/sign"} idOnly />
      <DocumentTemplateHeader 
        documents={data}
        totalDocuments={totalDocuments}
        activeDocuments={activeDocuments}
        completedDocuments={completedDocuments}
        inProgressDocuments={inProgressDocuments}
      />
      <FlatList
        data={rootDocuments} // Only show root level items
        renderItem={({ item, index }) => (
          <RenderItem
            hideDuplicate={false}
            item={item}
            index={index}
            onShare={() => linkGenModalRef.current?.openModal(item)}
            linkedChildren={data?.filter(child => child.parent === item?._id)}
            isChild={false}
          />
        )}
        keyExtractor={(item) => item._id}
        horizontal={false}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 100, paddingHorizontal: 10 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme ? '#64748b' : '#94a3b8'}
          />
        }
      />
    </DefaultScreen>
  )
}

// Local Styles
const localStyles = StyleSheet.create({
  documentContainer: {
    marginVertical: 8,
    width: '100%',
    maxWidth: '100%',
  },
  childDocumentContainer: {
    marginLeft: 24,
  },
  documentCard: {
    overflow: 'hidden',
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  contentSection: {
    gap: 16,
  },
  titleText: {
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 28,
  },
  metaSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  metaValue: {
    fontSize: 13,
    fontWeight: '500',
  },
  descriptionContainer: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  descriptionLight: {
    backgroundColor: 'rgba(241, 245, 249, 0.5)',
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  descriptionDark: {
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderColor: 'rgba(51, 65, 85, 0.8)',
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 20,
    fontStyle: 'italic',
  },
  creatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  creatorLight: {
    backgroundColor: 'rgba(241, 245, 249, 0.5)',
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  creatorDark: {
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderColor: 'rgba(51, 65, 85, 0.8)',
  },
  avatarCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
  },
  avatarLight: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  avatarDark: {
    backgroundColor: 'rgba(96, 165, 250, 0.2)',
    borderColor: 'rgba(96, 165, 250, 0.3)',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#3b82f6',
  },
  creatorLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  creatorName: {
    fontSize: 14,
    fontWeight: '600',
  },
  previewContainer: {
    marginTop: 16,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    minHeight: 120,
  },
  previewLight: {
    backgroundColor: 'rgba(241, 245, 249, 0.5)',
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  previewDark: {
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderColor: 'rgba(51, 65, 85, 0.8)',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  childrenContainer: {
    marginTop: 8,
    marginLeft: 32,
  },
  childItemWrapper: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  connectorContainer: {
    width: 32,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 24,
  },
  connectorLine: {
    width: 2,
    height: 24,
    borderRadius: 1,
  },
  connectorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: -4,
  },
  childItemContent: {
    flex: 1,
  },
});

export default DocumentTemplate;